import * as React from 'react';
import { useEffect, useState } from "react";
import { Theme, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import { TagDTO, TagType, TagsDTO } from "../../DTO/TagDTO";
import { ContrastColor, LightOrDarkColor } from "../../../services/LightOrDarkColor";
import Spinner from '../../../components/Spinner/Spinner';

enum Status {
  loading,
  standard,
}

const selectorWidth = 150
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: selectorWidth,
    },
  },
};

function getStyles(id: number, ids: readonly number[], theme: Theme) {
  return {
    fontWeight: ids.indexOf(id) !== -1
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

export function TagSelector(props: {
  kind: TagType,
  mode?: 'single' | 'list',
  selectedTags: TagDTO[],
  parentId: number,
  selectTag: (tag: TagDTO, single: boolean) => void,
  unsetTag: (tag: TagDTO) => void,
  readMode: boolean,
  className?: string,
  showLabel?: boolean,
}) {
  const theme = useTheme();
  const [status, setStatus] = useState(Status.standard)
  const [needle, setNeedle] = useState('')
  const [redraw, setRedraw] = useState(0)
  const [allTags] = useState(new TagsDTO({
    kind: props.kind,
  }))
  const [currentTags, setCurrentTags] = useState(new TagsDTO({
    tags: props.selectedTags,
    kind: props.kind,
  }))
  useEffect(() => {
    setCurrentTags(new TagsDTO({
      tags: props.selectedTags,
      kind: props.kind,
    }))
  }, [props.selectedTags, props.kind])

  const handleChange = async (event: SelectChangeEvent<number[]>) => {

    const {
      target: { value },
    } = event;
    console.log('value ey!', value, event)
    const activeIds = value as number[]

    const {add, remove} = currentTags.setTags(activeIds, allTags.tags, props.mode === 'single')
    const addedTags = add.map(tag => props.selectTag(tag, props.mode === 'single'))
    const removedTags = remove.map(tag => props.unsetTag(tag))
    await Promise.all([...addedTags, ...removedTags])
    setRedraw(redraw + 1)
  };

  const sxHideLegend = props.showLabel === false ? {
    '& .MuiOutlinedInput-notchedOutline legend': {
      width: 0,
      padding: 0,
      visibility: 'hidden',
    }
  } : {}

  return (
    
      <FormControl
        className={`${props.className || ''}`}
        sx={{
          m: 1,
          width: '100%',
          marginTop: 0,
          marginLeft: 0,
          ...sxHideLegend,
        }}
        hiddenLabel={props.showLabel === false}
      >
        {
          props.showLabel !== false &&
          <InputLabel
            shrink={true}
            id={`tagSelector-label-${props.kind}-${props.parentId}`}
            sx={{
              bgcolor: '#fff',
              paddingRight: '5px'
            }}
          >{currentTags.getKatergoryName()}</InputLabel>
        }
        <Select
          sx={{
            minHeight: '65px',
          }}
          labelId="demo-multiple-chip-label"
          id={`tagSelector-select-${props.kind}-${props.parentId}`}
          multiple
          value={currentTags.getTagIds()}
          onFocus={() => {
            if (props.readMode) { return }
            setStatus(Status.loading)
            allTags.getFromServerByKind(props.kind, true).then(() => {
              // setDrawer(!drawer)
              setStatus(Status.standard)
              // setRedraw(redraw + 1)
            })
          }}
          onChange={handleChange}
          input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {
                currentTags.getTags().map((ct, index) => {
                  return <Tag
                    key={`current-tag-${ct.id}-${index}`}
                    tag={ct}
                    unset={() => {
                      currentTags.removeTagLocal(ct.id)
                      props.unsetTag(ct)
                    }}
                    readmode={props.readMode}
                  />
                })
              }
            </Box>
          )}
          MenuProps={MenuProps}
        >
          {
            status === Status.loading &&
            <Spinner />
          }
          { status !== Status.loading &&
            allTags.getByNeedle(needle, []).map((tag, index) => {
              return <MenuItem
                key={tag.id}
                value={tag.id}
                style={getStyles(tag.id, currentTags.getTagIds(), theme)}
              >
                {tag.name}
              </MenuItem>
            })
          }
        </Select>
      </FormControl>
  );
}

export function Tag(props: {
  tag: TagDTO,
  onClick?: () => void,
  unset?: () => void,
  readmode?: boolean,
}) {
  return <Chip
    key={props.tag.id}
    label={props.tag.name}
    sx={{
      bgcolor: props.tag.color,
      color: ContrastColor(props.tag.color),
    }}
  />
  return <button
    className={`tag-bubble w3-round-xxlarge tag-color-${LightOrDarkColor(props.tag.color)}`}
    style={{
      backgroundColor: props.tag.color
    }}
  >
    <span
      className={`w3-padding-small ${props.readmode ? '' : 'cursor'}`}
      onClick={() => {
        if (props.onClick) {
          props.onClick()
        }
      }}
    >
      {props.tag.name}
    </span>
    {
      props.unset && !props.readmode && !props.tag.fixed &&
      <span
        className={`cursor deselect-tag w3-border-0`}
        onClick={() => {
          if (props.unset) {
            props.unset()
          }
        }}
      >&times;</span>
    }
  </button>
}


export function TagsColorsDisplay(props: {
  kind: TagType,
  selectedTags: TagDTO[],
  className?: string,
}) {
  const [currentTags, setCurrentTags] = useState(new TagsDTO({
    tags: props.selectedTags,
    kind: props.kind,
  }))
  useEffect(() => {
    setCurrentTags(new TagsDTO({
      tags: props.selectedTags,
      kind: props.kind,
    }))
  }, [props.selectedTags])
  return <div
    className={`${props.className || ''}`}
  >
    {
        currentTags.getTags().map((ct, index) => {
          return <div
            key={`current-tag-${ct.id}-${index}`}
            className="w3-padding-small tag-bubble w3-round-xxlarge "
            style={{
              backgroundColor: ct.color
            }}
          >&nbsp;</div>
        })
      }
  </div>
}
